import XGrowWithdrawalsService from "@services/resources/XGrowWithdrawalsService";
import XGrowWithdrawService from '@services/resources/XGrowWithdrawService';
import WithdrawalsAnalysisService from "@services/resources/WithdrawalsAnalysisService";
import Vue from 'vue';
import moment from "@/node_modules/moment/moment";
import WithdrawalStatus from "@views/withdrawals/new/enums/withdrawalStatus.js";

const service = XGrowWithdrawalsService.build();
const historyService = XGrowWithdrawService.build();
const statusService = WithdrawalsAnalysisService.build();

const initialIndicatorCard = () => ({
  type: '',
  duplicate: '',
  tooltip: '',
  filter: '',
  icon: null,
  title: '',
  sales: null,
  value: null,
  selected: false,
});

const initialSellerCard = () => ({
  path: '',
  key: '',
  second_key: '',
  third_key: '',
  icon: null,
  title: '',
  type: '',
  filter: ''
});

const initialSaleHistoryFilters = () => ({
  id: '',
  email: '',
  name: '',
  phone: '',
  document: '',
  indicatorCard: '',
  sellerCard: '',
  badge: ''
});

const initialFilters = () => ({
  name: '',
  email: '',
  document: '',
  status: null,
  currencyId: 1,
  created_after: '2025-07-29',
  created_before: '2025-07-31'
});

const state = {
  withdrawals: [],
  pagination: {
    currentPage: 1,
    perPage: 5,
    totalRows: 0
  },
  filters: initialFilters(),
  loading: false,
  loadingHistory: false,
  userId: null,
  withdrawalHistory: [],
  // Kept apart from the general `error`: the timeline must tell a failure from an empty history.
  historyError: false,
  pollingInterval: null,
  lastPollingTime: null,
  withdrawalGroups: null,
  withdrawalGroupData: [],
  indicatorCard: initialIndicatorCard(),
  sellerCard: initialSellerCard(),
  saleHistoryFilters: initialSaleHistoryFilters(),
  reviewWithdrawalTab: 'seller',
  activeIndicatorCardIds: [],
  activeFilterCardIds: [],
  groups: [],
  groupPagination: {
    currentPage: 1,
    perPage:     5,
    totalRows:   0,
    lastPage:    1,
    hasMore:     false,
  },
  groupLoading: false,
  groupError: null,
  withdrawalItemsByDate: {},
  withdrawalItemsLoading: false,
  withdrawalItemsError: null,
  withdrawalsPaginated:[],
  loadingWithdrawalsAnalysis: false,
  errorMessage: "",
  error: "",
  statusOptions: []
}

const getters = {
  xGrowWithdrawals: state => state.withdrawals,
  xGrowPagination: state => state.pagination,
  xGrowFilters: state => state.filters,
  xGrowLoading: state => state.loading,
  xGrowLoadingHistory: state => state.loadingHistory,
  xGrowErrorMessage: state => state.errorMessage,
  xGrowError: state => state.error,
  xGrowUserId: state => state.userId,
  xGrowWithdrawalHistory: state => state.withdrawalHistory,
  xGrowWithdrawalHistoryError: state => state.historyError,
  xGrowWithdrawalGroups: state => state.withdrawalGroups || { date: null, withdrawals: [] },
  xGrowWithdrawalGroupData: state => state.withdrawalGroupData || [],
  xGrowGetIndicatorCard: state => state.indicatorCard,
  xGrowGetSellerCard: state => state.sellerCard,
  xGrowGetSaleHistoryFilters: state => state.saleHistoryFilters,
  xGrowGetReviewWithdrawalTab: state => state.reviewWithdrawalTab,
  xGrowGetActiveIndicatorCardIds: state => state.activeIndicatorCardIds,
  xGrowGetActiveFilterCardIds: state => state.activeFilterCardIds,
  xGrowGroups: state => state.groups,
  xGrowGroupLoading: state => state.groupLoading,
  xGrowGroupError: state => state.groupError,
  xGrowGroupsPagination: state => state.groupPagination,

  xGrowWithdrawalItemsByDate: state => date =>
    state.withdrawalItemsByDate[date] || [],
  xGrowWithdrawalItemsLoading: state => state.withdrawalItemsLoading,
  xGrowWithdrawalItemsError:  state => state.withdrawalItemsError,
  xGrowWithdrawalsPaginated: state => state.withdrawalsPaginated,
  xGrowStatusOptions: state => state.statusOptions,
}

const mutations = {
  SET_WITHDRAWALS(state, withdrawals) {
    state.withdrawals = withdrawals;
  },

  SET_PAGINATION(state, payload) {
    state.pagination = { ...state.pagination, ...payload };
  },

  SET_FILTER(state, { filter, value }) {
    state.filters[filter] = value;
  },

  RESET_XGROW_WITHDRAWAL_FILTERS(state) {
    state.filters = initialFilters();
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_LOADING_HISTORY(state, loadingHistory) {
    state.loadingHistory = loadingHistory;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_USER_ID(state, userId) {
    state.userId = userId;
  },

  SET_WITHDRAWAL_HISTORY(state, history) {
    state.withdrawalHistory = history || [];
  },

  SET_SELLER_WITHDRAWAL_HISTORY(state, history) {
    state.withdrawalHistory = history || [];
  },
  XGROW_SET_HISTORY_ERROR(state, failed) {
    state.historyError = failed;
  },

  setWithdrawalSales(state, sales) {
    state.withdrawalSales = sales;
  },

  updateWithdrawal(state, { id, status, operator_id, updated_at }) {
    const groupIndex = state.withdrawals.findIndex(group =>
      group.withdrawals.some(withdrawal => withdrawal.id === id)
    );

    if (groupIndex !== -1) {
      const group = state.withdrawals[groupIndex];
      const withdrawalIndex = group.withdrawals.findIndex(withdrawal => withdrawal.id === id);

      if (withdrawalIndex !== -1) {
        const updatedWithdrawal = {
          ...group.withdrawals[withdrawalIndex],
          status,
          operator_id,
          updated_at,
        };

        Vue.set(state.withdrawals[groupIndex].withdrawals, withdrawalIndex, updatedWithdrawal);

      }
    }
  },

  SET_POLLING_INTERVAL(state, interval) {
    state.pollingInterval = interval;
  },

  SET_LAST_POLLING_TIME(state, time) {
    state.lastPollingTime = time;
  },

  SET_WITHDRAWAL_GROUPS(state, groupData) {
    Vue.set(state, 'withdrawalGroups', groupData || { date: null, withdrawals: [] });
  },

  SET_WITHDRAWAL_GROUP_DATA(state, groupData) {
    Vue.set(state, 'withdrawalGroupData', groupData || []);
  },

  RESET_INDICATOR_CARD(state) {
    state.indicatorCard = initialIndicatorCard();
  },

  SET_INDICATOR_CARD(state, payload) {
    state.indicatorCard = payload;
  },

  RESET_SELLER_CARD(state) {
    state.sellerCard = initialSellerCard();
  },

  SET_SELLER_CARD(state, payload) {
    state.sellerCard = payload;
  },

  RESET_SALE_HISTORY_FILTERS(state) {
    state.saleHistoryFilters = initialSaleHistoryFilters();
  },

  SET_SALE_HISTORY_FILTERS(state, payload) {
    if (!state.saleHistoryFilters) {
      state.saleHistoryFilters = {};
    }
    state.saleHistoryFilters = {
      ...state.saleHistoryFilters,
      [payload.key]: payload.value
    };
  },

  SET_REVIEW_WITHDRAWAL_TAB(state, payload) {
    state.reviewWithdrawalTab = payload;
  },

  addActiveIndicatorCardId(state, cardId) {
    if (!state.activeIndicatorCardIds.includes(cardId)) {
      state.activeIndicatorCardIds.push(cardId);
    }
  },

  removeActiveIndicatorCardId(state, cardId) {
    const index = state.activeIndicatorCardIds.indexOf(cardId);
    if (index > -1) {
      state.activeIndicatorCardIds.splice(index, 1);
    }
  },

  clearActiveIndicatorCardIds(state) {
    state.activeIndicatorCardIds = [];
  },

  addActiveFilterCardId(state, cardId) {
    if (!state.activeFilterCardIds.includes(cardId)) {
      state.activeFilterCardIds.push(cardId);
    }
  },

  removeActiveFilterCardId(state, cardId) {
    const index = state.activeFilterCardIds.indexOf(cardId);
    if (index > -1) {
      state.activeFilterCardIds.splice(index, 1);
    }
  },

  clearActiveFilterCardIds(state) {
    state.activeFilterCardIds = [];
  },

  // Grupos
  setGroupLoading(state, loading)   { state.groupLoading = loading },
  setGroupError(state, messageError)   { state.groupError = messageError },
  SET_GROUPS(state, groups)     { state.groups = groups },
  SET_GROUP_PAGINATION(state, payload) {
    state.groupPagination = {
      ...state.groupPagination,
      ...payload
    };
  },
  
  CLEAR_API_FLAG(state) {
    if (state.groupPagination._fromApi) {
      delete state.groupPagination._fromApi;
    }
  },
  // Itens de grupo
  SET_ITEMS_LOADING(state, loading)   { state.withdrawalItemsLoading = loading },
  SET_ITEMS_ERROR(state, messageError)   { state.withdrawalItemsError = messageError },
  SET_ITEMS_FOR_DATE(state, { date, items }) {
    Vue.set(state.withdrawalItemsByDate, date, items);
  },

  SET_LOADING_WITHDRAWALS_ANALYSIS(state, status){
    state.loadingWithdrawalsAnalysis = status;
  },

  APPEND_ITEMS_FOR_DATE(state, { date, items })  {
    const existing = state.withdrawalItemsByDate[date] || []
    Vue.set(
      state.withdrawalItemsByDate,
      date,
      existing.concat(items)
    )
  },

  RESET_ALL_WITHDRAWAL_ITEMS(state) {
    Vue.set(state, 'withdrawalItemsByDate', {});
  },

  SET_STATUSES(state, statuses) {
    state.statusOptions = statuses;
  },
}

const actions = {
  async xGrowFetchWithdrawals({ commit, state }, date = null) {
    commit('SET_LOADING', true);

    try {
      const {
        seller_name,
        seller_email,
        seller_document,
        status,
        currencyId,
        currentPage,
        perPage
      } = {
        ...state.filters,
        currentPage: state.pagination.currentPage,
        perPage:     state.pagination.perPage
      }

      const name     = seller_name;
      const email    = seller_email;
      const document = seller_document;

      const params = {
        page:        currentPage,
        per_page:    perPage,
        currency_id: currencyId,
      }
      if (date) {
        params.created_after = date;
        params.created_before = date;
      }

      if (status !== null) {
        params.status = status;
      } else if (state.filters.status) {
        params.status = state.filters.status;
      }
      if (name)     params.name     = name
      if (email)    params.email    = email
      if (document) params.document = document

      const response = await service.search(params)

      const payload = response.data
      const items = payload.withdrawals ? payload.withdrawals : []
      const respDate = payload.date ? payload.date : date

      commit('SET_ITEMS_FOR_DATE', { date: respDate, items })
      commit('SET_WITHDRAWALS', response.data)
      commit('SET_PAGINATION',  { totalRows: response.meta.total })

    } catch (error) {

      console.error(error);
      commit('SET_ERROR', 'Erro ao carregar saques')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async xGrowFetchWithdrawalGroup({ commit, state }, { date, status = null }) {
    commit('SET_LOADING', true);

    try {
      const params = {
        date,
        currency_id: state.filters.currencyId,
      };

      if (status) params.status = status;

      const response = await service.getGroupItems(new URLSearchParams(params).toString());

      const groupData = response.data || response;

      commit('SET_WITHDRAWAL_GROUPS', groupData);

      return groupData;
    } catch (error) {
      console.error("Erro ao carregar grupo:", error);
      commit('SET_ERROR', "Erro ao carregar grupo");
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async xGrowSetUserIdAction({ commit }, id) {
    commit('SET_USER_ID', id);
  },

  async xGrowFetchWithdrawalHistory({ commit, state }) {
    commit('SET_LOADING_HISTORY', true);
    try {
      const response = await historyService.read({
        id: `/history/${state.userId}`
      });

      commit('SET_WITHDRAWAL_HISTORY', response || response.data);
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
      commit('SET_ERROR', "Erro ao carregar histórico");
      commit('SET_WITHDRAWAL_HISTORY', []);
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

  xGrowUpdateFilter({ commit, dispatch }, { filter, value }) {
    commit('SET_FILTER', { filter, value });
    dispatch('xGrowFetchGroups');
  },

  xGrowUpdateMultipleFilters({ commit, dispatch }, filters) {
    for (const [filter, value] of Object.entries(filters)) {
      commit('SET_FILTER', { filter, value });
    }

    dispatch('xGrowFetchGroups');
  },

  xGrowUpdatePagination({ commit, dispatch }, { key, value, blockedSearch = false }) {
    commit('SET_PAGINATION', { [key]: value });

    if (!blockedSearch) {
      dispatch('xGrowFetchWithdrawals');
    }
  },

  xGrowUpdateGroupPagination({ commit, dispatch }, { key, value, blockedSearch = false }) {
    commit('SET_GROUP_PAGINATION', { [key]: value });
    if (!blockedSearch) {
      dispatch('xGrowFetchGroups');
    }
  },

  async xGrowUpdateWithdrawalStatus({ commit }, { id, status, operator_id = null, operator_name = null, reason = null }) {
    commit('SET_LOADING', true);

    try {
      const payload = { status };
      if (reason) {
        payload.reason = reason;
      }

      if (operator_id) {
        payload.operator_id = operator_id;
      }

      if (operator_name) {
        payload.operator_name = operator_name;
      }

      const response = await service.updateStatus(id, payload);

      return response.data || response;
    } catch (error) {
      console.error("Erro ao atualizar saque:", error);

      const errorMessage =
        (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
        error.message ||
        error.error ||
        "Erro ao atualizar status";

      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async xGrowCheckWithdrawalStatuses({ commit, state }, { withdrawalIds, date = null, status = null }) {
    try {
      const {
        name,
        email,
        document,
        seller_id,
        currency_id,
        status: filterStatus,
      } = state.filters;
      const response = await service.checkStatus({
        ids: withdrawalIds,
        date,
        status: status || filterStatus || null,
        name,
        email,
        document,
        seller_id,
        currency_id,
      });

      const groupData = Array.isArray(response.data) ? response.data[0] : response.data || response;

      commit('SET_WITHDRAWAL_GROUP_DATA', groupData);

      commit('SET_ITEMS_FOR_DATE', {
        date:      groupData.date,
        items:     groupData.withdrawals || []
      })

      return groupData;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      const errorMessage = (
        error.response
        && error.response.data
        && error.response.data.message
      ) || "Erro ao verificar status";
      commit('SET_ERROR', errorMessage);
    }
  },

  async xGrowGetWithdrawalAnalysis({ commit }, withdrawalId) {
    commit('SET_LOADING', true);

    try {
      const response = await service.getAnalysis(withdrawalId);
      return response.data || response;
    } catch (error) {
      console.error("Erro ao carregar análise:", error);
      const errorMessage = 
        (error.response && error.response.data && error.response.data.message) ||
        "Erro ao carregar análise do saque";
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async xGrowGetWithdrawalSales({ commit }, { withdrawalId, params = {} }) {
    commit('SET_LOADING_HISTORY', true);

    try {
      const response = await service.getSales(withdrawalId, params);
      return response.data || response;
    } catch (error) {
      console.error("Erro ao carregar vendas:", error);
      const errorMessage = 
        (error.response && error.response.data && error.response.data.message) ||
        "Erro ao carregar vendas do saque";
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

  async xGrowExportWithdrawals({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await service.blob({
        date:       payload.date,
        currency:   payload.currency,
        new_rules:  payload.new_rules
      });
      return response || response.data;

    } catch (error) {
      let errorMessage = 'Não foi possível exportar os saques';
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errObj = JSON.parse(text);
          errorMessage = errObj.msg || errObj.message || errorMessage;
        } catch (parseErr) {
          console.error('Falha ao converter Blob de erro em texto:', parseErr);
        }
      }

      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  xGrowSelectIndicatorCardAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_INDICATOR_CARD');
    }
    commit('SET_INDICATOR_CARD', payload);
  },

  xGrowSelectSellerCardAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_SELLER_CARD');
    }
    commit('SET_SELLER_CARD', payload);
  },

  xGrowUpdateSaleHistoryFilterAction({ commit }, payload) {
    if (payload === null) {
      return commit('RESET_SALE_HISTORY_FILTERS');
    }
    commit('SET_SALE_HISTORY_FILTERS', payload);
  },

  xGrowUpdateReviewWithdrawalTabAction({ commit }, payload) {
    commit('SET_REVIEW_WITHDRAWAL_TAB', payload);
  },

  xGrowAddActiveIndicatorCardIdAction({ commit }, cardId) {
    commit('addActiveIndicatorCardId', cardId);
  },

  xGrowRemoveActiveIndicatorCardIdAction({ commit }, cardId) {
    commit('removeActiveIndicatorCardId', cardId);
  },

  xGrowClearActiveIndicatorCardIdsAction({ commit }) {
    commit('clearActiveIndicatorCardIds');
  },

  xGrowAddActiveFilterCardIdAction({ commit }, cardId) {
    commit('addActiveFilterCardId', cardId);
  },

  xGrowRemoveActiveFilterCardIdAction({ commit }, cardId) {
    commit('removeActiveFilterCardId', cardId);
  },

  xGrowClearActiveFilterCardIdsAction({ commit }) {
    commit('clearActiveFilterCardIds');
  },

  async xGrowFetchGroups({ commit, state }, statusArg = null) {
    commit('setGroupLoading', true)
    commit('setGroupError', null)
    commit('RESET_ALL_WITHDRAWAL_ITEMS');

    try {
      const { name, email, document, status, seller_id, analyst_name } = state.filters

      const params = {
        page:       state.groupPagination.currentPage || 1,
        per_page:   state.groupPagination.perPage    || 5,
        currency_id: state.filters.currencyId        || 1
      }

      if (name)     params.name     = name
      if (email)    params.email    = email
      if (document) params.document = document
      if (seller_id) params.seller_id = seller_id
      if (analyst_name) params.analyst_name = analyst_name
      if (statusArg) {
        params.status = statusArg
      } else if (status) {
        params.status = status
      }

      const response = await service.groups(new URLSearchParams(params).toString())

      const payload = response.meta ? response : response.data

      const groups = payload.data
      const meta   = payload.meta

      commit('SET_GROUPS', groups)
      // Não trigger dispatches automáticos quando atualizar paginação da response
      commit('SET_GROUP_PAGINATION', {
        currentPage: meta.page,
        perPage:     meta.per_page,
        totalRows:   meta.total,
        lastPage:    meta.last_page,
        hasMore:     meta.current_page < meta.last_page,
        _fromApi:    true  // Flag para identificar que veio da API
      })
      return groups
    }
    catch (err) {
      console.error('Erro ao carregar grupos:', err)
      commit('setGroupError', 'Falha ao carregar grupos')
      throw err
    }
    finally {
      commit('setGroupLoading', false)
    }
  },

  async xGrowFetchGroupItems({ commit, state }, { date, statusArg = null }) {
    commit('SET_ITEMS_LOADING', true)
    commit('SET_ITEMS_ERROR', null)

    try {
      const { name, email, document, status, seller_id, analyst_name } = state.filters
      const params = {
        created_after: date || null,
        created_before:  date || null,
        limit: 50
      }

      if (name)     params.name             = name
      if (email)    params.email            = email
      if (document) params.document         = document
      if (seller_id) params.seller_id       = seller_id
      if (analyst_name) params.analyst_name = analyst_name

      if (statusArg) {
        params.status = statusArg
      } else if (status) {
        params.status = status
      }

      const response = await service.getGroupWithdrawals(new URLSearchParams(params).toString())

      if (!response || !response.data) {
        throw new Error("Resposta inválida da API")
      }

      const payload = response.data
      const items = payload.withdrawals ? payload.withdrawals : []
      const respDate = payload.date ? payload.date : date

      commit('SET_ITEMS_FOR_DATE', { date: respDate, items })
      return { date: respDate, items }
    }
    catch (err) {
      console.error(`Erro ao carregar itens do grupo ${date}:`, err)
      commit('SET_ITEMS_ERROR', 'Falha ao carregar itens')
      throw err
    }
    finally {
      commit('SET_ITEMS_LOADING', false)
    }
  },

  async xGrowFetchStatuses({ commit }) {
    try {
      const response = await statusService.getStatuses();
      const list = Array.isArray(response) ? response : [];
      const allowed = new Set(WithdrawalStatus.getHasXGrowWithdrawal() || []);
      const payload = list.filter(item => allowed.has(item.value));

      commit('SET_STATUSES', payload);
      return payload;
    } catch (err) {
      console.error('Erro ao buscar status:', err);
      return [];
    }
  },

  async xGrowGetGroupPaginated({ commit }, { date, last_id = null, status = null, search = null, currency_id = null, limit = 50 }) {
    commit('SET_LOADING_WITHDRAWALS_ANALYSIS', true)
    try {
      const params = new URLSearchParams()
      params.append('created_after', date || null);
      params.append('created_before', date ? moment(date).add(1, 'day').format('YYYY-MM-DD') : null);
      params.append('limit', limit)

      if (last_id)     params.append('last_id', last_id)
      if (status)      params.append('status', status)
      if (search)      params.append('search', search)
      if (currency_id) params.append('currency_id', currency_id)

      const response = await service.getGroupWithdrawals(params.toString())
      const { date: respDate, withdrawals } = response.data

      commit('APPEND_ITEMS_FOR_DATE', { date: respDate, items: withdrawals })

      return { date: respDate, withdrawals }
    } catch (err) {
      console.error('Erro ao carregar mais itens:', err)
      throw err
    } finally {
      commit('SET_LOADING_WITHDRAWALS_ANALYSIS', false)
    }
  },

  async xGrowFetchWithdrawalSalesFiltered({ commit, state }) {
    commit('SET_LOADING_HISTORY', true);
    try {
      const withdrawalId = state.selectedWithdrawalId;
      const rawFilters = state.saleHistoryFilters;

      const filters = {
        page: 1,
        per_page: 10,
        ...Object.entries(rawFilters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[`filter[${key}]`] = value;
          }
          return acc;
        }, {})
      };

      const response = await service.getSalesWithFilters(withdrawalId, filters);

      commit('setWithdrawalSales', response.data || []);
    } catch (error) {
      console.error("Erro ao buscar vendas filtradas:", error);
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

  async xGrowFetchSellerHistory({ commit }, { sellerId, params = {} }) {
    commit('SET_LOADING_HISTORY', true);
    commit('XGROW_SET_HISTORY_ERROR', false);
    try {
      const response = await service.getSellerHistory(sellerId, params);
      const history = response.data || response;
      
      // Store history in state for the history-table component
      commit('SET_SELLER_WITHDRAWAL_HISTORY', history);
      
      return history;
    } catch (error) {
      console.error("Erro ao buscar histórico do seller:", error);
      const errorMessage = 
        (error.response && error.response.data && error.response.data.message) ||
        "Erro ao carregar histórico do seller";
      commit('SET_ERROR', errorMessage);
      commit('XGROW_SET_HISTORY_ERROR', true);
      commit('SET_SELLER_WITHDRAWAL_HISTORY', []);
      throw error;
    } finally {
      commit('SET_LOADING_HISTORY', false);
    }
  },

}

export {
  state,
  getters,
  mutations,
  actions
}
